import { Chart as ChartJS, ChartData, ScriptableContext } from 'chart.js';
import { DateTime } from 'luxon';

import APIError from 'types/api_error';
import { DimensionInput } from 'dashboards/types/dimension';
import { FilterInput } from 'dashboards/types/filter';
import { RequireField } from 'types/utility_types';
import {
  TimeRangeInput,
  TimeSeriesGranularity,
  TimeSeriesResponse,
} from 'dashboards/types/time';

import {
  Hsla,
  hslaToString,
  MAIN_CHART_COLOR,
} from 'dashboards/services/color';
import { useChartColors } from 'dashboards/hooks/useChartColors';
import { useTimeRangeParams } from 'dashboards/hooks/useTimeRangeParams';
import { useTimeSeries } from 'dashboards/hooks/useTimeSeries';

type UseHeatmapChartParams = {
  metricName: string;
  granularity: TimeSeriesGranularity;
  timeRange: TimeRangeInput;
  dimensions: Array<DimensionInput>;
  filters: Array<FilterInput>;
  timeZone: string;
};

type DataType = ChartData<
  'matrix',
  Array<{ x: string; y: string; v: number }>,
  string
>;

type HeatmapChartResult =
  | {
      seriesLabels: undefined;
      data: undefined;
      isLoading: boolean;
      error?: APIError | null;
    }
  | {
      seriesLabels: string[];
      data: RequireField<DataType, 'labels'>;
      isLoading: boolean;
      error?: APIError | null;
    };

export function useHeatmapChart(
  params: UseHeatmapChartParams
): HeatmapChartResult {
  const { timezone } = useTimeRangeParams();

  const {
    data,
    isLoading: isLoadingTimeSeries,
    error,
  } = useTimeSeries({ params });

  const { data: colors, isLoading: isLoadingColors } = useChartColors(
    data?.map(({ labels, values, ...headers }) =>
      String(Object.values(headers)[0])
    )
  );

  const { granularity } = params;
  const isLoading = isLoadingTimeSeries || isLoadingColors;

  if (!data || !colors) {
    return { data: undefined, seriesLabels: undefined, isLoading, error };
  }

  // FIXME Remove this once fixed in api-server
  if (data.length === 0) {
    return {
      data: undefined,
      seriesLabels: undefined,
      isLoading,
      error: { message: 'No data available yet.', code: '', status: 0 },
    };
  }

  const seriesLabels = data
    .map(({ labels, values, ...headers }) => String(Object.values(headers)[0]))
    .filter(Boolean);

  const labelColors = Object.fromEntries(
    seriesLabels.map((label, index) => [label, colors[index]])
  );

  return {
    seriesLabels,
    data:
      granularity === 'HOUR'
        ? createChartData(
            seriesLabels,
            groupByHourOfDay(timezone, data),
            labelColors
          )
        : createChartData(
            seriesLabels,
            groupByDayOfWeek(timezone, data),
            labelColors
          ),
    isLoading,
    error,
  };
}

const HOURS_OF_DAY = Array.from({ length: 24 }, (_, i) =>
  DateTime.fromObject({ year: 2000, month: 1, day: 1, hour: i }).toFormat('ha')
);

/**
 * Group data by hour of day.
 */
function groupByHourOfDay(timezone: string, data: TimeSeriesResponse) {
  return data.map(({ labels, values }) => {
    const groupedData: { [k: string]: number } = {};

    labels.forEach((label, i) => {
      const timeGroup = DateTime.fromISO(label, { zone: timezone }).toFormat(
        'ha'
      );
      groupedData[timeGroup] =
        (groupedData[timeGroup] || 0) + Number(values[i]);
    });

    return {
      labels: HOURS_OF_DAY,
      values: HOURS_OF_DAY.map((label) => groupedData[label]),
    };
  });
}

const DAYS_OF_WEEK = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

/**
 * Group data by day of week.
 */
function groupByDayOfWeek(timezone: string, data: TimeSeriesResponse) {
  return data.map(({ labels, values }) => {
    const groupedData: { [k: string]: number } = {};

    labels.forEach((label, i) => {
      const timeGroup =
        DAYS_OF_WEEK[DateTime.fromISO(label, { zone: timezone }).weekday % 7];
      groupedData[timeGroup] =
        (groupedData[timeGroup] || 0) + Number(values[i]);
    });

    return {
      labels: DAYS_OF_WEEK,
      values: DAYS_OF_WEEK.map((label) => groupedData[label]),
    };
  });
}

/**
 * Create chart data for the `matrix` chart type.
 */
function createChartData(
  seriesLabels: string[],
  data: Array<{ labels: Array<string>; values: Array<number> }>,
  colors: Record<string, Hsla>
) {
  const [{ labels }] = data;

  const dataPoints = data.flatMap(({ labels, values }, index) =>
    labels.map((label, i) => ({
      x: label,
      y: seriesLabels[index],
      v: values[i] ?? 0,
    }))
  );

  const minV = Math.min(...dataPoints.map((dataPoint) => dataPoint?.v || 0));
  const maxV = Math.max(...dataPoints.map((dataPoint) => dataPoint?.v || 0));

  const datasets = [
    {
      label: 'Dataset',
      borderRadius: 2,
      data: dataPoints,
      backgroundColor(context: ScriptableContext<'matrix'>) {
        const dataPoint = context.dataset.data[context.dataIndex];
        // The `matrix` chart type uses the `v` property but it's not defined in the types so we can't use explicit types here
        const value = (dataPoint as any).v;
        const label = String(dataPoint.y);
        const color = colors[label] ?? MAIN_CHART_COLOR;
        const alpha = (value - minV) / (maxV - minV);
        return hslaToString({ ...color, a: alpha });
      },
      width: ({ chart }: { chart: ChartJS }) =>
        (chart.chartArea || {}).width / labels.length - 1,
      height: ({ chart }: { chart: ChartJS }) =>
        (chart.chartArea || {}).height / seriesLabels.length - 1,
    },
  ];

  return { seriesLabels, labels, datasets };
}
